import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient, { ApiResponse, PaginationMeta } from "./apiClient";

export interface User {
  uid: string;
  display_name: string;
  email: string;
  status: "Active" | "InActive";
  is_enabled: boolean;
}

export interface UserPayload {
  display_name: string;
  email?: string;
  is_enabled?: boolean;
}

export interface PaginatedUsers {
  staff: User[];
  pagination: PaginationMeta;
}

const QUERY_KEY = ["users"];

const fetchUsers = async (page: number): Promise<PaginatedUsers> => {
  const { data: response } = await apiClient.get<ApiResponse<{ staff: User[]; pagination: PaginationMeta }>>(
    "/v1/admin/staff",
    { params: { page } }
  );
  return response.data;
};

export const useUsers = (page: number = 1) =>
  useQuery({
    queryKey: [...QUERY_KEY, page],
    queryFn: () => fetchUsers(page),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

const addUser = async (body: UserPayload): Promise<User> => {
  const { data: response } = await apiClient.post<ApiResponse<User>>("/v1/admin/staff", body);
  return response.data;
};

export const useAddUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const updateUser = async ({ uid, ...body }: UserPayload & { uid: string }): Promise<User> => {
  const { data: response } = await apiClient.put<ApiResponse<User>>("/v1/admin/staff", { uid, ...body });
  return response.data;
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const deleteUser = async (uid: string): Promise<void> => {
  await apiClient.delete(`/v1/admin/staff/${uid}`);
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const resetUserPassword = async (uid: string): Promise<void> => {
  await apiClient.post("/v1/admin/staff/reset-password", { uid });
};

export const useResetUserPassword = () =>
  useMutation({ mutationFn: resetUserPassword });
